import { get } from 'lodash'
import * as querystring from 'query-string'
import { ThunkAction } from 'redux-thunk'

import studioRecommendationsConfig from '../../config/sites/studio-recommendations'
import { noIndex, setErrorPage, setSeoMeta, showAlert } from '../actions/common'
import fetchRecommendedSearchesByCategoryResults from '../actions/search/fetchRecommendedSearchesByCategoryResults'
import fetchRecommendedSearchesByTermResults from '../actions/search/fetchRecommendedSearchesByTermResults'
import axiosVerbose from '../utils/axios/axiosVerbose'
import {
  logError,
  ResponseDataError,
  SearchOfflineError
} from '../utils/errors'
import getProxyApiUrl from '../utils/getProxyApiUrl'
import {
  buildSearchQueryString,
  isCategoryOnlySearch
} from '../utils/searchQueryHelper'
import { parseLocation } from '../utils/urlHelper'
import setStudioRecommendations from './search/setStudioRecommendations'

import { getItemMatrix } from '../utils/itemMatrix'

import { IRootState } from '../../common/reducers'

import { ILoadDataOptions } from '../../typings/loadDataOptions'
import { IRecommendedSearches } from '../../typings/recommendedSearches'
import { IProxySearchResults } from '../../typings/searchResults'
import {
  ISearchHeadings,
  ISearchIngestedContent
} from '../../typings/seoMetadata'
import { IStudioRecommendations } from '../../typings/studioRecommendations'

export enum TypeKeys {
  GET_SEARCH_RESULTS_REQUEST = 'GET_SEARCH_RESULTS_REQUEST',
  GET_SEARCH_RESULTS_SUCCESS = 'GET_SEARCH_RESULTS_SUCCESS',
  GET_SEARCH_RESULTS_FAIL = 'GET_SEARCH_RESULTS_FAIL',

  GET_INGESTED_CONTENT_SUCCESS = 'GET_INGESTED_CONTENT_SUCCESS',

  SET_SEARCH_HEADINGS = 'SET_SEARCH_HEADINGS',
  SET_ACTIVE_AUDIO = 'SET_ACTIVE_AUDIO',
  SET_HOSTING_SETUP_SEARCH = 'SET_HOSTING_SETUP_SEARCH',
  SET_STUDIO_RECOMMENDATIONS = 'SET_STUDIO_RECOMMENDATIONS',

  GET_ITEM_LICENCES_SUCCESS = 'GET_ITEM_LICENCES_SUCCESS',
  GET_ITEM_LICENCES_FAIL = 'GET_ITEM_LICENCES_FAIL',

  GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_REQUEST = 'GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_REQUEST',
  GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_SUCCESS = 'GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_SUCCESS',
  GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_FAIL = 'GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_FAIL',

  GET_RECOMMENDED_SEARCHES_BY_CATEGORY_RESULTS_SUCCESS = 'GET_RECOMMENDED_SEARCHES_BY_CATEGORY_RESULTS_SUCCESS',
  GET_RECOMMENDED_SEARCHES_BY_CATEGORY_RESULTS_FAIL = 'GET_RECOMMENDED_SEARCHES_BY_CATEGORY_RESULTS_FAIL',

  GET_SIMILAR_ITEMS_SUCCESS = 'GET_SIMILAR_ITEMS_SUCCESS',
  GET_SIMILAR_ITEMS_FAIL = 'GET_SIMILAR_ITEMS_FAIL'
}

export interface IGetSearchResultsRequestAction {
  type: TypeKeys.GET_SEARCH_RESULTS_REQUEST
}

export interface IGetSearchResultsSuccessAction {
  type: TypeKeys.GET_SEARCH_RESULTS_SUCCESS
  payload: Readonly<{ results: IProxySearchResults }>
}

export interface IGetSearchResultsFailAction {
  type: TypeKeys.GET_SEARCH_RESULTS_FAIL
  error: boolean
  httpStatus: number
  timeout: boolean
}

export interface IGetIngestedContentSuccessAction {
  type: TypeKeys.GET_INGESTED_CONTENT_SUCCESS
  payload: Readonly<{ content: ISearchIngestedContent }>
}

export interface ISetSearchHeadingsAction {
  type: TypeKeys.SET_SEARCH_HEADINGS
  payload: Readonly<ISearchHeadings>
}

export interface ISetActiveAudioAction {
  type: TypeKeys.SET_ACTIVE_AUDIO
  payload: Readonly<{ activeAudio: string }>
}

export interface ISetHostingSetupSearchAction {
  type: TypeKeys.SET_HOSTING_SETUP_SEARCH
  payload: Readonly<{ hostingSetupSearch: boolean }>
}

export interface IGetItemLicencesSuccessAction {
  type: TypeKeys.GET_ITEM_LICENCES_SUCCESS
  payload: Readonly<{ itemID: number; licenses: any }>
}

export interface IGetItemLicencesFailAction {
  type: TypeKeys.GET_ITEM_LICENCES_FAIL
  payload: Readonly<{ itemID: number; error: any }>
}

export interface IGetRecommendedSearchesByTermResultsRequestAction {
  type: TypeKeys.GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_REQUEST
}

export interface IGetRecommendedSearchesByTermResultsSuccessAction {
  type: TypeKeys.GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_SUCCESS
  payload: Readonly<IRecommendedSearches>
}

export interface IGetRecommendedSearchesByTermResultsFailAction {
  type: TypeKeys.GET_RECOMMENDED_SEARCHES_BY_TERM_RESULTS_FAIL
  error: boolean
}

export interface IGetRecommendedSearchesByCategoryResultsSuccessAction {
  type: TypeKeys.GET_RECOMMENDED_SEARCHES_BY_CATEGORY_RESULTS_SUCCESS
  payload: Readonly<IRecommendedSearches>
}

export interface IGetRecommendedSearchesByCategoryResultsFailAction {
  type: TypeKeys.GET_RECOMMENDED_SEARCHES_BY_CATEGORY_RESULTS_FAIL
}

export interface ISetStudioRecommendationsAction {
  type: TypeKeys.SET_STUDIO_RECOMMENDATIONS
  payload: Readonly<IStudioRecommendations>
}

export interface IGetSimilarItemsSuccessAction {
  type: TypeKeys.GET_SIMILAR_ITEMS_SUCCESS
  payload: Readonly<{ itemID: number; similarItems: any }>
}

export interface IGetSimilarItemsFailAction {
  type: TypeKeys.GET_SIMILAR_ITEMS_FAIL
  payload: Readonly<{ itemID: number; error: string }>
}

export type ActionTypes =
  | IGetSearchResultsRequestAction
  | IGetSearchResultsSuccessAction
  | IGetSearchResultsFailAction
  | IGetIngestedContentSuccessAction
  | ISetSearchHeadingsAction
  | ISetActiveAudioAction
  | ISetHostingSetupSearchAction
  | IGetItemLicencesSuccessAction
  | IGetItemLicencesFailAction
  | IGetRecommendedSearchesByTermResultsRequestAction
  | IGetRecommendedSearchesByTermResultsSuccessAction
  | IGetRecommendedSearchesByTermResultsFailAction
  | IGetRecommendedSearchesByCategoryResultsSuccessAction
  | IGetRecommendedSearchesByCategoryResultsFailAction
  | ISetStudioRecommendationsAction
  | IGetSimilarItemsSuccessAction
  | IGetSimilarItemsFailAction

const fetchSearchResults = (
  parsedUrl: any,
  opts: ILoadDataOptions
): ThunkAction<Promise<void>, IRootState, void> => {
  return (dispatch, getState) => {
    const parsedLocation = parseLocation({
      pathname: parsedUrl.pathname,
      query: querystring.parse(parsedUrl.search)
    })

    const currentSite = get(getState(), 'common.currentSite') || 'themeforest'
    const apiUrlPrefix = getProxyApiUrl(opts)
    const searchStringQuery = buildSearchQueryString(
      parsedLocation,
      currentSite
    )
    const hasItemId = parsedLocation.item_id
    const hasSearchTerm = parsedLocation.term

    const searchUrl = hasItemId
      ? `${apiUrlPrefix}/shopfront_api/search/similar?${searchStringQuery}`
      : `${apiUrlPrefix}/shopfront_api/search?${searchStringQuery}`

    dispatch(fetchSearchResultsRequest())

    if (hasSearchTerm && !hasItemId) {
      dispatch(
        fetchRecommendedSearchesByTermResults(parsedLocation.term, {
          apiUrlPrefix
        })
      )
    }

    return axiosVerbose
      .get<IProxySearchResults>(searchUrl)
      .then(res => {
        if (res.data && res.data.matches) {
          if (res.data.metadata) {
            if (typeof document !== 'undefined') {
              document.title = res.data.metadata.title
            }
            dispatch(setSearchHeadings(res.data.metadata))
            dispatch(setSeoMeta(res.data.metadata))
          }

          dispatch(fetchSearchResultsSuccess(res.data))
          dispatch(fetchIngestedContentSuccess(res.data.searchIngestedContent))
          dispatch(
            setStudioRecommendations(
              studioRecommendationsConfig[currentSite] || {}
            )
          )
          dispatch(setHostingSetupSearch(parsedLocation))

          // process other types of recommendations if term is not present
          if (isCategoryOnlySearch(parsedLocation)) {
            dispatch(
              fetchRecommendedSearchesByCategoryResults(
                parsedLocation,
                res.data,
                currentSite
              )
            )
          }
        } else {
          throw ResponseDataError({ response: res })
        }
      })
      .catch(err => {
        const isSearchOffline = [422, 500, 502, 503].includes(err.statusCode)
        if (isSearchOffline) {
          throw SearchOfflineError({
            statusCode: err.statusCode,
            message: `HTTP ${err.statusCode}. ${err.message}`,
            responseData: err.responseData
          })
        }

        throw err
      })
      .catch(err => Promise.reject(logError(err)))
      .catch(err => {
        return Promise.reject(dispatch(fetchSearchResultsFailure(err)))
      })
  }
}

/**
 * Search result actions
 */
const fetchSearchResultsRequest = () => {
  const action: IGetSearchResultsRequestAction = {
    type: TypeKeys.GET_SEARCH_RESULTS_REQUEST
  }
  return action
}
const fetchSearchResultsSuccess = (results: IProxySearchResults) => {
  const action: IGetSearchResultsSuccessAction = {
    type: TypeKeys.GET_SEARCH_RESULTS_SUCCESS,
    payload: { results }
  }
  return action
}
const fetchSearchResultsFailure = (
  error: any
): ThunkAction<IGetSearchResultsFailAction, IRootState, void> => {
  return dispatch => {
    let timeout = false

    dispatch(noIndex())

    if (error.type === 'search.offline') {
      dispatch(setErrorPage('SearchOffline'))
    } else {
      if (error.type === 'axios.timeout') {
        timeout = true
      }
      dispatch(showAlert(error.message, 'failure', false))
    }

    const action: IGetSearchResultsFailAction = {
      type: TypeKeys.GET_SEARCH_RESULTS_FAIL,
      error: true,
      httpStatus: 500,
      timeout
    }

    return dispatch(action)
  }
}

/**
 * IngestedContentSuccess
 */
const fetchIngestedContentSuccess = (content: ISearchIngestedContent) => {
  const action: IGetIngestedContentSuccessAction = {
    type: TypeKeys.GET_INGESTED_CONTENT_SUCCESS,
    payload: { content }
  }
  return action
}

/**
 * Search headings
 */
const setSearchHeadings = ({
  heading,
  sub_heading,
  fine_print
}: ISearchHeadings) => {
  const action: ISetSearchHeadingsAction = {
    type: TypeKeys.SET_SEARCH_HEADINGS,
    payload: {
      heading,
      sub_heading,
      fine_print
    }
  }
  return action
}

export const setActiveAudio = (activeAudio: string) => {
  const action: ISetActiveAudioAction = {
    type: TypeKeys.SET_ACTIVE_AUDIO,
    payload: { activeAudio }
  }
  return action
}

export const setHostingSetupSearch = (parsedLocation: any) => {
  const action: ISetHostingSetupSearchAction = {
    type: TypeKeys.SET_HOSTING_SETUP_SEARCH,
    payload: { hostingSetupSearch: parsedLocation.hosting_setup === '1' }
  }
  return action
}

/**
 * Search item licenses
 */
export const fetchItemLicenses = (
  itemID: number
): ThunkAction<
  Promise<IGetItemLicencesSuccessAction | IGetItemLicencesFailAction>,
  IRootState,
  void
> => {
  return (dispatch, getState) => {
    const licenses = get(getState(), 'searchPage.licenses')
    const errorOccured = !!(licenses[itemID] && licenses[itemID].error)

    if (!licenses[itemID] || errorOccured) {
      return getItemMatrix(itemID)
        .then(licensesData => {
          return dispatch(
            fetchItemLicensesSuccess({ itemID, licenses: licensesData })
          )
        })
        .catch(error => {
          return dispatch(fetchItemLicensesFailure(itemID, error.message))
        })
    } else {
      return Promise.resolve(
        dispatch(
          fetchItemLicensesSuccess({ itemID, licenses: licenses[itemID] })
        )
      )
    }
  }
}

const fetchItemLicensesSuccess = ({
  itemID,
  licenses
}: {
  itemID: number
  licenses: any
}) => {
  const action: IGetItemLicencesSuccessAction = {
    type: TypeKeys.GET_ITEM_LICENCES_SUCCESS,
    payload: { itemID, licenses }
  }
  return action
}

const fetchItemLicensesFailure = (itemID: number, error: any) => {
  const action: IGetItemLicencesFailAction = {
    type: TypeKeys.GET_ITEM_LICENCES_FAIL,
    payload: { itemID, error }
  }
  return action
}

export default fetchSearchResults



// WEBPACK FOOTER //
// ./common/actions/searchPage.ts